import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export function Reset() {
    const navigate = useNavigate();
    const [token, setToken] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const t = params.get('token');
        if (!t) {
            setError('El enlace no es válido o ya fue usado.');
        } else {
            setToken(t);
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); return; }
        if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }
        if (!token) { setError('Token inválido.'); return; }

        setError('');
        setLoading(true);

        try {
            const res = await api.resetPassword(token, password);
            if (res.data?.success) {
                setSuccess('¡Contraseña actualizada! Ahora podés iniciar sesión.');
                setTimeout(() => navigate('/login', { replace: true }), 2500);
            } else {
                setError(res.data?.error || 'No se pudo actualizar la contraseña.');
            }
        } catch (err) {
            const apiError = err.response?.data?.error;
            setError(apiError || 'Error de conexión. Intentá nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-sp-light flex items-center justify-center px-4 xs:px-5 py-8">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-[400px]">
                <div className="bg-white rounded-3xl p-6 xs:p-8 shadow-sp">
                    <div className="text-center mb-6 xs:mb-8">
                        <div className="w-14 h-14 xs:w-16 xs:h-16 bg-gradient-to-br from-sp-primary to-sp-accent rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <Lock className="text-white w-7 h-7 xs:w-8 xs:h-8" />
                        </div>
                        <h1 className="text-[1.4rem] xs:text-[1.6rem] font-black text-sp-text mb-1">Nueva contraseña</h1>
                        <p className="text-sp-muted text-[0.85rem] xs:text-[0.9rem] font-semibold">Elegí una contraseña segura</p>
                    </div>

                    {success ? (
                        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4">
                            <CheckCircle className="w-12 h-12 text-sp-cta mx-auto mb-3" />
                            <p className="text-sp-text font-bold text-[0.95rem] mb-4">{success}</p>
                            <Link to="/login" className="inline-block w-full h-11 bg-sp-primary text-white rounded-5xl font-extrabold text-[0.95rem] leading-[44px] shadow-cta">
                                Ir al login
                            </Link>
                        </motion.div>
                    ) : (
                        <>
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-600 text-[0.85rem] font-semibold px-4 py-3 rounded-xl mb-4 flex items-center gap-2">
                                    <span>⚠️</span> {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                                <div>
                                    <label className="block text-[0.8rem] xs:text-[0.85rem] font-extrabold text-sp-text mb-1.5">Nueva contraseña</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sp-primary/50 w-4 h-4" />
                                        <input
                                            type={showPass ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Mínimo 6 caracteres"
                                            autoComplete="new-password"
                                            disabled={loading || !token}
                                            className="w-full h-11 xs:h-12 pl-10 pr-12 rounded-xl border-2 border-sp-light bg-sp-light/50 text-sp-text text-[0.9rem] font-semibold placeholder:text-sp-muted/50 focus:outline-none focus:border-sp-primary focus:bg-white transition-all disabled:opacity-50"
                                        />
                                        <button type="button" onClick={() => setShowPass((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-sp-primary/60">
                                            {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[0.8rem] xs:text-[0.85rem] font-extrabold text-sp-text mb-1.5">Repetir contraseña</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sp-primary/50 w-4 h-4" />
                                        <input
                                            type={showConfirm ? 'text' : 'password'}
                                            value={confirm}
                                            onChange={(e) => setConfirm(e.target.value)}
                                            placeholder="Repetí la contraseña"
                                            autoComplete="new-password"
                                            disabled={loading || !token}
                                            className="w-full h-11 xs:h-12 pl-10 pr-12 rounded-xl border-2 border-sp-light bg-sp-light/50 text-sp-text text-[0.9rem] font-semibold placeholder:text-sp-muted/50 focus:outline-none focus:border-sp-primary focus:bg-white transition-all disabled:opacity-50"
                                        />
                                        <button type="button" onClick={() => setShowConfirm((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-sp-primary/60">
                                            {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <button type="submit" disabled={loading || !password || !confirm || !token} className="w-full h-11 xs:h-12 bg-sp-primary text-white rounded-5xl font-extrabold text-[0.95rem] shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 active:scale-[0.98] transition-all disabled:opacity-60">
                                    {loading ? 'Guardando...' : 'Guardar contraseña'}
                                </button>
                            </form>
                        </>
                    )}

                    <div className="text-center mt-5">
                        <Link to="/login" className="inline-flex items-center gap-1.5 text-[0.8rem] xs:text-[0.85rem] font-bold text-sp-muted hover:text-sp-primary transition-colors">
                            <ArrowLeft className="w-3.5 h-3.5" /> Volver al login
                        </Link>
                    </div>
                </div>
            </motion.div>
        </main>
    );
}